import type { ConsentManager, ConsentSnapshot } from '../types'
import {
  getDefaultConsentForHost,
  hostUsesCookieYesConsent,
} from './hostConsent'

declare global {
  interface Window {
    dataLayer?: any[]
    __viCookieYesDataLayerPatched?: boolean
  }
}

function coerceCookieYesFlag(value: any): boolean {
  // CookieYes cookies often store "yes"/"no" strings.
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'yes' || v === 'true' || v === '1') return true
    if (v === 'no' || v === 'false' || v === '0') return false
  }
  return Boolean(value)
}

function snapshotFromCookieYesDetail(detail: any): ConsentSnapshot {
  // CookieYes event detail typically looks like:
  // { necessary: true, functional: false, analytics/statistics: true, advertisement/marketing: false, ... }
  // On old site we used e.detail.statistics.
  const statistics = coerceCookieYesFlag(
    detail?.statistics ?? detail?.analytics
  )
  const marketing = coerceCookieYesFlag(
    detail?.marketing ?? detail?.advertisement
  )
  const functional = coerceCookieYesFlag(detail?.functional)

  return { functional, statistics, marketing }
}

function parseCookieYesConsentCookie(value: string): any | null {
  const decoded = decodeURIComponent(value)

  try {
    return JSON.parse(decoded)
  } catch {
    // CookieYes commonly stores consent as comma-separated key/value pairs:
    // consentid:...,analytics:yes,advertisement:no,functional:yes
    const pairs = decoded.split(',').map(part => part.trim())
    const parsed: Record<string, string> = {}

    pairs.forEach(pair => {
      const separatorIndex = pair.indexOf(':')
      if (separatorIndex <= 0) return

      const key = pair.slice(0, separatorIndex).trim()
      const pairValue = pair.slice(separatorIndex + 1).trim()
      if (key) parsed[key] = pairValue
    })

    return Object.keys(parsed).length > 0 ? parsed : null
  }
}

function readCookie(name: string): string | null {
  try {
    if (typeof document === 'undefined') return null
    const match = document.cookie
      .split('; ')
      .reverse()
      .find(row => row.startsWith(`${name}=`))
    if (!match) return null
    return match.substring(name.length + 1)
  } catch {
    return null
  }
}

function isConsentDataLayerItem(item: any): boolean {
  if (!item) return false
  if (item.event === 'cookie_consent_update') return true
  return item[0] === 'consent' && item[1] === 'update'
}

function installCookieYesDataLayerBridge() {
  if (typeof window === 'undefined') return
  if (window.__viCookieYesDataLayerPatched) return

  window.dataLayer = window.dataLayer || []
  const dataLayer = window.dataLayer
  const originalPush = dataLayer.push.bind(dataLayer)

  dataLayer.push = (...items: any[]) => {
    const result = originalPush(...items)
    if (items.some(isConsentDataLayerItem)) {
      window.setTimeout(() => {
        window.dispatchEvent(new Event('vi_cookieyes_consent_update'))
      }, 0)
    }
    return result
  }

  window.__viCookieYesDataLayerPatched = true
}

function sameSnapshot(a: ConsentSnapshot, b: ConsentSnapshot): boolean {
  return (
    a.functional === b.functional &&
    a.statistics === b.statistics &&
    a.marketing === b.marketing
  )
}

export class CookieYesConsentManager implements ConsentManager {
  private snapshot: ConsentSnapshot = getDefaultConsentForHost(null)
  private listeners = new Set<(snapshot: ConsentSnapshot) => void>()

  constructor(host?: string | null) {
    if (typeof window === 'undefined') return

    const currentHost = host ?? window.location.host
    this.snapshot = getDefaultConsentForHost(currentHost)

    if (!hostUsesCookieYesConsent(currentHost)) return

    installCookieYesDataLayerBridge()

    // Returning users may already have consent stored in the CookieYes cookie.
    // We read it up-front so destinations can initialize on the first page view.
    this.refreshFromCookie()

    window.addEventListener('cookieyes_consent_update', (e: any) => {
      this.setSnapshot(snapshotFromCookieYesDetail(e?.detail))
      window.setTimeout(() => this.refreshFromCookie(), 0)
    })

    window.addEventListener('vi_cookieyes_consent_update', () => {
      this.refreshFromCookie()
    })
  }

  private setSnapshot(next: ConsentSnapshot) {
    if (sameSnapshot(this.snapshot, next)) return
    this.snapshot = next
    this.listeners.forEach(cb => cb(next))
  }

  private refreshFromCookie() {
    const cookieValue = readCookie('cookieyes-consent')
    if (!cookieValue) return

    const parsed = parseCookieYesConsentCookie(cookieValue)
    if (parsed) {
      this.setSnapshot(snapshotFromCookieYesDetail(parsed))
    }
  }

  getSnapshot(): ConsentSnapshot {
    return this.snapshot
  }

  onChange(cb: (snapshot: ConsentSnapshot) => void): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }
}
